!function () {
  'use strict';

  function getVariantMediaMap(productInfo) {
    const mapNode = productInfo.querySelector('[data-variant-media-map]');
    if (!mapNode) return [];

    try {
      return JSON.parse(mapNode.textContent);
    } catch (error) {
      console.warn('variant-media-sync: invalid variant map', error);
      return [];
    }
  }

  function getCurrentVariant(productInfo) {
    const variantNode = productInfo.querySelector('variant-selects [data-selected-variant]');
    if (!variantNode) return null;

    try {
      return JSON.parse(variantNode.textContent);
    } catch (error) {
      return null;
    }
  }

  function getMediaIdFromSlide(slide) {
    if (!slide) return null;

    const mediaId = Number(slide.getAttribute('data-id'));
    return Number.isFinite(mediaId) ? mediaId : null;
  }

  function findVariantForMedia(variantMap, mediaId, currentVariant) {
    const matches = variantMap.filter((variant) => Number(variant.featured_media_id) === mediaId);
    if (!matches.length) return null;
    if (matches.length === 1) return matches[0];

    const currentOptionIds = currentVariant
      ? variantMap.find((variant) => variant.id === currentVariant.id)?.option_value_ids || []
      : [];

    return matches.reduce((best, candidate) => {
      const overlap = candidate.option_value_ids.filter((id) => currentOptionIds.includes(id)).length;
      const score = overlap + (candidate.available ? 100 : 0);
      const bestScore =
        best.option_value_ids.filter((id) => currentOptionIds.includes(id)).length + (best.available ? 100 : 0);

      return score > bestScore ? candidate : best;
    });
  }

  function setPopoutValue(variantSelects, optionValueId, optionValue) {
    const popoutItem = variantSelects.querySelector(`[data-popout-list] [data-option-value-id="${optionValueId}"]`);
    if (!popoutItem) return null;

    const option = popoutItem.querySelector('[data-popout-option]');
    const popout = popoutItem.closest('popout-select');
    const hiddenInput = popout?.querySelector('[data-popout-input]');
    const displayText = popout?.querySelector('[data-popout-text]');
    const value = option?.dataset.value || optionValue;

    if (hiddenInput && value) {
      hiddenInput.value = value;
      hiddenInput.dataset.optionValueId = String(optionValueId);
      hiddenInput.dataset.productUrl = popoutItem.dataset.productUrl || '';
    }

    if (displayText) {
      displayText.textContent = option?.querySelector('span')?.textContent?.trim() || value || displayText.textContent;
    }

    popout?.querySelectorAll('.select-popout__item--current').forEach((item) => {
      item.classList.remove('select-popout__item--current');
    });
    popoutItem.classList.add('select-popout__item--current');
    popout?.querySelectorAll('[aria-current="true"]').forEach((item) => item.removeAttribute('aria-current'));
    option?.setAttribute('aria-current', 'true');

    return hiddenInput;
  }

  function setOptionValue(scope, optionValueId, optionValue) {
    const radio = scope.querySelector(`input[data-option-value-id="${optionValueId}"]`);
    if (radio) {
      if (radio.type === 'radio') radio.checked = true;
      return radio;
    }

    if (optionValue) {
      const inputByValue = scope.querySelector(
        `[data-single-option-selector][value="${CSS.escape(optionValue)}"]`
      );
      if (inputByValue) {
        if (inputByValue.type === 'radio') inputByValue.checked = true;
        return inputByValue;
      }
    }

    return setPopoutValue(scope.closest('variant-selects') || scope, optionValueId, optionValue);
  }

  function selectVariant(productInfo, variant) {
    const variantSelects = productInfo.querySelector('variant-selects');
    if (!variantSelects) return false;

    const wrappers = [...variantSelects.querySelectorAll('[data-option-position]')].sort(
      (a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition)
    );

    let changedInput = null;

    if (variant.option_value_ids?.length) {
      wrappers.forEach((wrapper, index) => {
        const optionValueId = variant.option_value_ids[index];
        if (!optionValueId) return;

        const input = setOptionValue(wrapper, optionValueId, variant.options?.[index]);
        if (input) changedInput = input;
      });
    }

    if (!changedInput && variant.options?.length) {
      wrappers.forEach((wrapper, index) => {
        const optionValue = variant.options[index];
        if (!optionValue) return;

        const input = wrapper.querySelector(
          `[data-single-option-selector][value="${CSS.escape(optionValue)}"]`
        );
        if (!input) return;

        if (input.type === 'radio') input.checked = true;
        changedInput = input;
      });
    }

    if (!changedInput) return false;

    productInfo._syncingVariantFromMedia = true;
    changedInput.dispatchEvent(new Event('change', { bubbles: true }));
    window.setTimeout(() => {
      productInfo._syncingVariantFromMedia = false;
    }, 0);

    return true;
  }

  function syncVariantFromMedia(productInfo, slide) {
    if (!productInfo || productInfo._syncingVariantFromMedia) return;

    const mediaId = getMediaIdFromSlide(slide);
    if (!mediaId) return;

    const currentVariant = getCurrentVariant(productInfo);
    if (Number(currentVariant?.featured_media?.id) === mediaId) return;

    const variantMap = getVariantMediaMap(productInfo);
    const nextVariant = findVariantForMedia(variantMap, mediaId, currentVariant);
    if (!nextVariant) return;

    selectVariant(productInfo, nextVariant);
  }

  function handleMediaVisible(event) {
    const wrapper = event.target.closest?.('[data-product-single-media-wrapper]');
    if (!wrapper) return;

    const productInfo = wrapper.closest('product-info');
    if (!productInfo) return;

    syncVariantFromMedia(productInfo, wrapper);
  }

  function selectInitialMediaSlide(productInfo) {
    const slider = productInfo.querySelector('[data-product-single-media-slider]');
    if (!slider) return;

    const initialMediaId = slider.dataset.initialMediaId;
    if (!initialMediaId) return;

    const selectSlide = () => {
      const flkty = window.theme?.Flickity?.data(slider);
      if (!flkty || !flkty.isActive) return false;

      const slide = slider.querySelector(`[data-product-single-media-wrapper][data-id="${initialMediaId}"]`);
      if (!slide) return true;

      const index = [...slide.parentNode.children].indexOf(slide);
      if (index < 0) return true;

      if (flkty.selectedIndex !== index) {
        flkty.select(index, false, true);
      }

      const thumbs = productInfo.querySelector('[data-product-single-media-thumbs]');
      const flktyNav = thumbs ? window.theme?.Flickity?.data(thumbs) : null;
      if (flktyNav?.isActive && flktyNav.selectedIndex !== index) {
        flktyNav.select(index, false, true);
      }

      return true;
    };

    if (selectSlide()) return;

    let attempts = 0;
    const retry = window.setInterval(() => {
      attempts += 1;
      if (selectSlide() || attempts >= 20) window.clearInterval(retry);
    }, 250);
  }

  function attachFlickitySync(productInfo) {
    const slider = productInfo.querySelector('[data-product-single-media-slider]');
    if (!slider) return;

    const attach = () => {
      const flkty = window.theme?.Flickity?.data(slider);
      if (!flkty || flkty._variantMediaSyncAttached) return Boolean(flkty);

      flkty.on('settle', () => {
        syncVariantFromMedia(productInfo, flkty.selectedElement);
      });

      flkty._variantMediaSyncAttached = true;
      return true;
    };

    if (attach()) return;

    let attempts = 0;
    const retry = window.setInterval(() => {
      attempts += 1;
      if (attach() || attempts >= 20) window.clearInterval(retry);
    }, 250);
  }

  function initProductInfo(productInfo) {
    if (!productInfo || !productInfo.querySelector('[data-variant-media-map]')) return;

    if (productInfo.dataset.variantMediaSyncInit !== 'true') {
      productInfo.dataset.variantMediaSyncInit = 'true';
      productInfo.addEventListener('theme:media:visible', handleMediaVisible);
    }

    attachFlickitySync(productInfo);
    selectInitialMediaSlide(productInfo);
  }

  function initAll() {
    document.querySelectorAll('product-info').forEach(initProductInfo);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('theme:product-info:loaded', (event) => {
    const productInfo = event.target?.closest?.('product-info') || event.target;
    if (productInfo?.matches?.('product-info')) initProductInfo(productInfo);
  });
}();
